export interface KPIData {
	TPH: number | null;
	TPHActualAvg: number | null;
	TPHBudget: number | null;
	TPHTarget: number | null;
	TPHAvgLastHour: number | null;
	Uptime: number;
	UptimeMillsOn: number;
	HoursAvailable: number;
	UptimePercentage: number | null;
	AvgTPHDuringUptime: number | null;
	PushRateActual: number | null;
	PushRateWithDelays: number | null;
	PushRateScheduled: number | null;
	Productivity: number | null;
	ProductivityOverTarget: number | null;
	// KPI Percentages (actual / target * 100)
	TPHPct: number | null;
	UptimePct: number | null;
	PushRatePct: number | null;
	// TPH Thresholds
	TPHRed: number;
	TPHOrange: number;
	TPHYellow: number;
	TPHGreenSoft: number;
	TPHGreenNormal: number;
	TPHGreenHard: number;
	// Uptime Thresholds
	UptimeRed: number;
	UptimeOrange: number;
	UptimeYellow: number;
	UptimeGreenSoft: number;
	UptimeGreenNormal: number;
	UptimeGreenHard: number;
	// Push Rate Thresholds
	PushRateRed: number;
	PushRateOrange: number;
	PushRateYellow: number;
	PushRateGreenSoft: number;
	PushRateGreenNormal: number;
	PushRateGreenHard: number;
	// Productivity Thresholds
	ProductivityRed: number;
	ProductivityOrange: number;
	ProductivityYellow: number;
	ProductivityGreenSoft: number;
	ProductivityGreenNormal: number;
	ProductivityGreenHard: number;
}

const tonsAtPace = (tph: number | null, data: KPIData): number | null =>
	data.HoursAvailable ? (tph ?? 0) * data.HoursAvailable : null;

const formatTons = (value: number) => Math.round(value).toLocaleString("en-US");

const trim = (value: number | null, decimals: number) =>
	parseFloat((value ?? 0).toFixed(decimals));

export const actualTons = (data: KPIData): string => {
	const tons = tonsAtPace(data.TPH, data);
	return tons === null ? "-" : formatTons(tons);
};

export const targetTons = (data: KPIData): string => {
	if (!data.TPHTarget) return "-";
	const tons = tonsAtPace(data.TPHTarget, data);
	return tons === null ? "-" : formatTons(tons);
};

export const actualTonsTooltip = (data: KPIData, period: string): string => {
	const tons = tonsAtPace(data.TPH, data);
	if (tons === null) return `No hours have elapsed yet in this ${period}.`;
	return `Rolling at the actual pace of ${trim(data.TPH, 1)} TPH for the ${trim(data.HoursAvailable, 2)} hours elapsed adds up to ${formatTons(tons)} tons this ${period}.`;
};

export const targetTonsTooltip = (data: KPIData, period: string): string => {
	if (!data.TPHTarget)
		return `No target TPH is set for this ${period}, so target tons cannot be calculated.`;

	const target = tonsAtPace(data.TPHTarget, data);
	if (target === null) return `No hours have elapsed yet in this ${period}.`;

	const delta = Math.round((tonsAtPace(data.TPH, data) ?? 0) - target);
	const gap =
		delta === 0
			? "Actual is exactly on target."
			: delta > 0
				? `Actual is ${formatTons(delta)} tons ahead.`
				: `Actual is ${formatTons(-delta)} tons behind.`;

	return `Holding the target pace of ${trim(data.TPHTarget, 1)} TPH for those same ${trim(data.HoursAvailable, 2)} hours would reach ${formatTons(target)} tons. ${gap}`;
};

export const productivityDuringUptime = (data: KPIData): number | null => {
	const { AvgTPHDuringUptime, TPHTarget, Uptime } = data;
	if (!Uptime) return null;
	if (AvgTPHDuringUptime === null || TPHTarget === null || !TPHTarget)
		return null;
	return (AvgTPHDuringUptime / TPHTarget) * 100;
};
